import { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, TextField, Typography,
} from '@mui/material';
import { plural, pluralWord } from '../../../lib/plural';
import { tripDateLabel } from '../../../lib/reminderPlan';
import {
  allShiftKeys,
  defaultShiftSelection,
  failureLine,
  shiftKey,
  shiftPhrase,
  type DateShiftMove,
  type DateShiftPlan,
} from '../../../lib/tripDates';
import { MAX_MOVE_DAYS, type MoveResult, type TripDatesOffer } from './useDateShiftSession';

/** "8 guests have claimed seats" / "1 guest has claimed a seat" */
export const claimedSeatsLine = (guests: number): string => (
  `${plural(guests, 'guest')} ${guests === 1 ? 'has claimed a seat' : 'have claimed seats'}`
);

/**
 * The opt-in that follows a saved change of the trip's start date: move the
 * itinerary events and dinners by the same number of days, or leave them. It is
 * also the way back in later ("Move events to match the trip dates" on the card)
 * for events a date change left outside the trip. That link only ever offers items
 * OUTSIDE the trip dates, so the note in this dialog must not promise it for an item
 * left behind that still falls inside them.
 *
 * The dates are ALREADY saved when this opens after a change, so there is no "cancel the
 * change" here, only "leave these as they are". Every row starts UNTICKED: a host
 * who dismisses this by pressing the primary button must never have moved an
 * event they did not choose (an earlier review rejected ticked defaults). Each
 * row shows the old and the new local time in the zone guests read it in. A moved
 * event keeps its local time; only the date moves. Nothing is sent to anyone
 * from here: the caller offers one change notice AFTER the moves, and "Don't send"
 * is its default.
 *
 * A move that partly fails does not end here: the dialog says what landed and what
 * did not, and "Try the N that didn't move again" re-plans only those from a fresh
 * read (see replanFailed), so a retry can never shift something twice.
 */
export default function TripDatesMoveDialog({
  plan,
  offer,
  busy,
  error,
  result,
  onDays,
  onLeave,
  onConfirm,
  onRetry,
  onDone,
}: {
  plan: DateShiftPlan;
  offer: TripDatesOffer;
  busy: boolean;
  /** Why the last attempt did nothing (the itinerary could not be re-read): the ticks are still here, press Move again. */
  error: string;
  /** Set once an attempt has left something behind. */
  result: MoveResult | null;
  /** Present when the shift is the host's to set (the way back in). */
  onDays?: (days: number) => void;
  /** "Leave them as they are": the dates stay saved, nothing else changes. */
  onLeave: () => void;
  onConfirm: (selected: DateShiftMove[]) => void;
  onRetry: () => void;
  onDone: () => void;
}) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set(defaultShiftSelection()));
  const rows: DateShiftMove[] = [...plan.events, ...plan.dinners];
  const chosen = rows.filter((move) => selected.has(shiftKey(move)));
  const count = chosen.length;
  const cancelled = plan.skipped.filter((skip) => skip.reason === 'cancelled');
  const unmovable = plan.skipped.filter((skip) => skip.reason !== 'cancelled');
  const catchUp = offer.mode === 'catch_up';

  const toggle = (move: DateShiftMove, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(shiftKey(move));
    else next.delete(shiftKey(move));
    setSelected(next);
  };

  const section = (title: string, moves: DateShiftMove[]) => (moves.length === 0 ? null : (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>{title}</Typography>
      <Stack component="ul" spacing={0.5} sx={{ pl: 0, my: 0.5, listStyle: 'none' }}>
        {moves.map((move) => (
          <Box component="li" key={shiftKey(move)}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={(
                <Checkbox
                  checked={selected.has(shiftKey(move))}
                  disabled={busy}
                  onChange={(event) => toggle(move, event.target.checked)}
                  sx={{ pt: 0.5 }}
                  inputProps={{ 'aria-label': `Move ${move.label}` }}
                />
              )}
              label={(
                <Box>
                  <Typography variant="body2">{move.summary}</Typography>
                  {move.kind === 'dinner' && move.claimedSpots > 0 ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {claimedSeatsLine(move.claimedSpots)}
                    </Typography>
                  ) : null}
                  {move.stillOutside ? (
                    <Typography variant="caption" color="warning.main" display="block">still outside after this move</Typography>
                  ) : null}
                  {move.gapNote ? (
                    <Typography variant="caption" color="text.secondary" display="block">{move.gapNote}</Typography>
                  ) : null}
                </Box>
              )}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  ));

  const guestLine = offer.published
    ? (catchUp
      ? "Nothing is sent to guests from here. If anything moves, you'll be asked once whether to let guests know, and ‘Don’t send’ is the default."
      : "Nothing is sent to guests from here. When you're done you'll be asked once whether to let guests know, and ‘Don’t send’ is the default.")
    : "This trip isn't published, so guests aren't told anything.";

  const resultView = result ? (
    <Stack spacing={1.5}>
      <Typography>
        {result.moved > 0 ? `${plural(result.moved, 'item')} moved.` : 'Nothing moved yet.'}
        {result.failed.length > 0
          ? ` ${plural(result.failed.length, 'item')} did not move and ${pluralWord(result.failed.length, 'stays', 'stay')} on the old date:`
          : ''}
      </Typography>
      {result.failed.length > 0 ? (
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          {result.failed.map((failure) => <li key={`${failure.move.kind}:${failure.move.id}`}>{failureLine(failure)}</li>)}
        </Box>
      ) : null}
      {result.changed.length > 0 ? (
        <Alert severity="info">
          Left alone, because {pluralWord(result.changed.length, 'it', 'they')} no longer {pluralWord(result.changed.length, 'matches', 'match')} what was planned:
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {result.changed.map((failure) => <li key={`${failure.move.kind}:${failure.move.id}`}>{failureLine(failure)}</li>)}
          </Box>
        </Alert>
      ) : null}
    </Stack>
  ) : null;

  return (
    <Dialog open onClose={busy ? undefined : (result ? onDone : onLeave)} fullWidth maxWidth="sm">
      <DialogTitle>{catchUp ? 'Move events to match the trip dates' : 'Move the schedule too?'}</DialogTitle>
      <DialogContent>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {resultView ?? (
          <>
            {catchUp ? (
              <Typography sx={{ mb: 1 }}>
                These events and dinners fall outside the trip dates (the trip starts {tripDateLabel(offer.newStart)}); anything already
                inside the dates is not listed. Choose how many days to move them: the earliest lands on the start date by default.
                Each keeps its local time and changes only its date. Unticked items stay exactly as they are, and a rehearsal
                dinner the day before is a fine thing to leave.
              </Typography>
            ) : (
              <Typography sx={{ mb: 1 }}>
                The trip now starts {tripDateLabel(offer.newStart)}, {shiftPhrase(plan.days)} than {tripDateLabel(offer.oldStart ?? offer.newStart)}. The new
                dates are saved. Tick what should move by the same {plural(Math.abs(plan.days), 'day')}: each keeps its local time
                and changes only its date. Unticked items stay exactly as they are.
              </Typography>
            )}
            {onDays ? (
              <TextField
                size="small"
                type="number"
                label="Move by (days, negative is earlier)"
                value={offer.days}
                disabled={busy}
                onChange={(event) => { const n = Number.parseInt(event.target.value, 10); if (Number.isInteger(n)) onDays(n); }}
                inputProps={{ step: 1, min: -MAX_MOVE_DAYS, max: MAX_MOVE_DAYS }}
                sx={{ mb: 1, maxWidth: 260 }}
              />
            ) : null}
            {rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nothing would move by that many days.</Typography>
            ) : (
              <>
                {rows.some((move) => move.stillOutside) ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Rows marked &ldquo;still outside after this move&rdquo; are left out of Select all: change the number of days, or tick them yourself.
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                  <Button size="small" disabled={busy} onClick={() => setSelected(allShiftKeys(plan))}>Select all</Button>
                  <Button size="small" disabled={busy} onClick={() => setSelected(new Set())}>Select none</Button>
                </Stack>
                {section('Itinerary events', plan.events)}
                {section('Dinners', plan.dinners)}
              </>
            )}

            {cancelled.length > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {plural(cancelled.length, 'cancelled event')} stay{pluralWord(cancelled.length, 's', '')} where {pluralWord(cancelled.length, 'it is', 'they are')} —
                a cancellation is retained history and cannot be edited here.
              </Typography>
            ) : null}
            {unmovable.length > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {plural(unmovable.length, 'event')} cannot be moved automatically ({unmovable.map((skip) => skip.label).join(', ')}) and
                stay{pluralWord(unmovable.length, 's', '')} where {pluralWord(unmovable.length, 'it is', 'they are')}.
              </Typography>
            ) : null}
          </>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          {guestLine}
          {!catchUp && !result ? ' Anything that ends up outside the trip dates can be moved later from the Trip dates card (“Move events to match the trip dates”).' : ''}
        </Alert>
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button onClick={onDone} disabled={busy}>Done</Button>
            {result.failed.length > 0 ? (
              <Button variant="contained" disabled={busy} onClick={onRetry}>
                {busy ? 'Moving…' : `Try the ${result.failed.length} that didn't move again`}
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <Button onClick={onLeave} disabled={busy}>{catchUp ? 'Cancel' : 'Leave them as they are'}</Button>
            <Button variant="contained" disabled={busy || count === 0} onClick={() => onConfirm(chosen)}>
              {busy ? 'Moving…' : count > 0 ? `Move ${plural(count, 'item')}` : 'Nothing selected'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
